/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import { DSIcon } from '@spotahome/ui-library/base';
import { useCityName } from '@spotahome/ui-library';

import '@spotahome/marketplace-common/src/components/ListingContent.scss';

import { PROPERTY_TENANT_TYPE } from '../utils/propertyRulesHelper';

import ListingTitle from './ListingTitle';

import './DocumentsRequired.scss';

const GenericDocumentsRequiredSection = () => (
  <Fragment>
    <div
      className="documents-required__proof"
      data-test="document-section-generic"
    >
      <DSIcon name="id" className="documents-required__icon" />
      <p
        className="documents-required__title"
        dangerouslySetInnerHTML={{
          __html: trans('documents-required.identity-title')
        }}
      />
    </div>

    <div className="documents-required__proof">
      <DSIcon name="proof_of_income" className="documents-required__icon" />
      <p className="documents-required__title">
        {trans('documents-required.income-title')}
      </p>
    </div>
  </Fragment>
);

const StudentsOrWorkersDocumentsRequiredSection = props => (
  <div
    className="documents-required__section listing-content"
    data-test="document-section-students-or-workers"
  >
    <SectionForStudentsOrWorkers {...props} />
  </div>
);

const WORKERS_CONTENT = [
  'documents-required.identity-title',
  'documents-required.income-title-with-clarification',
  'documents-required.residence-permit-with-clarifications'
];

const STUDENTS_CONTENT = [
  'documents-required.identity-title',
  'documents-required.income-title-from-guarantor-with-clarification',
  'documents-required.university-acceptance'
];

const WORKER_HEADER = 'documents-required.section-details.profesional';
const STUDENT_HEADER = 'documents-required.section-details.student';

const StudentsOrWorkersHeader = ({ title }) => (
  <p
    className="documents-required__section-title"
    dangerouslySetInnerHTML={{
      __html: trans(title)
    }}
  />
);

StudentsOrWorkersHeader.propTypes = {
  title: PropTypes.string.isRequired
};

const StudentsOrWorkersContent = ({ items }) => (
  <ul className="documents-required__section-content">
    {items.map(item => (
      <li
        key={item}
        dangerouslySetInnerHTML={{
          __html: trans(item)
        }}
      />
    ))}
  </ul>
);

StudentsOrWorkersContent.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired
};

const SectionForStudentsOrWorkers = ({ onlyFor }) => {
  switch (onlyFor) {
    case PROPERTY_TENANT_TYPE.WORKERS:
      return (
        <div className="documents-required__section listing-content">
          <StudentsOrWorkersContent items={WORKERS_CONTENT} />
        </div>
      );
    case PROPERTY_TENANT_TYPE.STUDENTS:
      return (
        <div className="documents-required__section listing-content">
          <StudentsOrWorkersContent items={STUDENTS_CONTENT} />
        </div>
      );
    default:
      return (
        <div className="documents-required__section listing-content">
          <StudentsOrWorkersHeader title={WORKER_HEADER} />
          <StudentsOrWorkersContent items={WORKERS_CONTENT} />
          <StudentsOrWorkersHeader title={STUDENT_HEADER} />
          <StudentsOrWorkersContent items={STUDENTS_CONTENT} />
        </div>
      );
  }
};

SectionForStudentsOrWorkers.propTypes = {
  onlyFor: PropTypes.oneOf(Object.values(PROPERTY_TENANT_TYPE)).isRequired
};

function getDocumentsPerCountryComponent(countryCode) {
  switch (countryCode) {
    case 'DE':
      return GenericDocumentsRequiredSection;
    case 'FR':
      return StudentsOrWorkersDocumentsRequiredSection;
    default:
      return null;
  }
}

const DocumentsRequired = ({
  cityId,
  countryCode,
  onlyFor = PROPERTY_TENANT_TYPE.ANY
}) => {
  const [expanded, setExpanded] = useState(false);
  const cityName = useCityName(cityId);

  const DocumentsRequiredPerCountryComponent =
    getDocumentsPerCountryComponent(countryCode);

  const handleClick = e => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  return DocumentsRequiredPerCountryComponent ? (
    <Fragment>
      <div className="documents-required">
        <ListingTitle role="secondary">
          {trans('documents-required.title')}
        </ListingTitle>
        <p className="documents-required__instructions">
          {trans('documents-required.instructions')}
        </p>

        <DocumentsRequiredPerCountryComponent onlyFor={onlyFor} />

        <a
          role="button"
          tabIndex={0}
          className="documents-required__view-more"
          onClick={handleClick}
        >
          {expanded
            ? trans('documents-required.view-expanded')
            : trans('documents-required.view-collapsed')}
        </a>

        {expanded && (
          <Fragment>
            <p className="documents-required__details">
              {trans('documents-required.more-details', {
                city: cityName
              })}
            </p>
            <p className="documents-required__details">
              {trans('documents-required.details-visibility')}
            </p>
          </Fragment>
        )}
      </div>
    </Fragment>
  ) : null;
};

DocumentsRequired.propTypes = {
  cityId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  onlyFor: PropTypes.string
};

export default DocumentsRequired;
